import { SelectOptionType } from '@octano/global-ui';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import useSelectOptions from '../../../../../../../hooks/useSelectOptions';
import { PeriodType } from '../../../../../../../types/sectionMaintainerTypes';
import { getSelectedOption } from '../../../../../../../utils/selectFormat';
import { ScheduleSectionForm } from '../../../../../../SectionsMaintainer/parts/ModalMaintainerSchedule';
import { ScheduleSectionFormData } from '../../../../../../SectionsMaintainer/parts/ModalMaintainerSchedulePre';
import { ScheduleList } from '../../../../../../SectionsMaintainer/parts/SectionForm';
import { formatModuleLabel } from '../../../../../../SectionsMaintainer/utils';
import { NonStructuredSectionSchedulesResponse } from '../../../../../SectionsEdit/types';
import ModalMaintainerSchedule from './ModalMaintainerSchedule';

type ModalMaintainerSchedulePreProps = {
  sectionId: number;
  closeModal: () => void;
  onSave: (
    values: ScheduleSectionFormData,
    index?: number,
  ) => void | Promise<void>;
  scheduleIndex?: number;
  schedule?: ScheduleList;
  activities: NonStructuredSectionSchedulesResponse['activities'];
  campuses: NonStructuredSectionSchedulesResponse['campuses'];
  modules: NonStructuredSectionSchedulesResponse['timeModules'];
  responsibles: NonStructuredSectionSchedulesResponse['teachers'];
  modalities: NonStructuredSectionSchedulesResponse['modalities'];
  periodicities: NonStructuredSectionSchedulesResponse['periodicities'];
  period?: PeriodType;
};

const ModalMaintainerSchedulePre = ({
  sectionId,
  closeModal,
  onSave,
  scheduleIndex,
  schedule,
  activities,
  campuses,
  modules,
  responsibles,
  modalities,
  periodicities,
  period,
}: ModalMaintainerSchedulePreProps) => {
  const { daysOptions } = useSelectOptions();
  /**
   * Se obtienen las opciones de los selects
   * para realizar las validaciones de fechas
   */
  const options = useMemo<
    Record<
      | 'activityOptions'
      | 'campusOptions'
      | 'moduleStartOptions'
      | 'moduleEndOptions'
      | 'responsibleOptions'
      | 'modalityOptions'
      | 'periodicityOptions',
      SelectOptionType[]
    >
  >(() => {
    return {
      activityOptions: activities.map((act) => {
        return {
          value: act.id,
          label: act.name,
        };
      }),
      campusOptions: campuses.map((campus) => {
        return {
          value: campus.id,
          label: campus.name,
        };
      }),
      moduleStartOptions: modules.map((mod) => {
        return {
          value: mod.id,
          label: formatModuleLabel(mod, 'startTime'),
        };
      }),
      moduleEndOptions: modules.map((mod) => {
        return {
          value: mod.id,
          label: formatModuleLabel(mod, 'endTime'),
        };
      }),
      responsibleOptions: responsibles.map((prof) => {
        return {
          value: prof.account?.id ?? 0,
          label: prof.account?.fullName || '',
          account: prof.account,
        };
      }),
      modalityOptions:
        modalities.map((mod) => {
          return {
            value: mod.id,
            label: mod.name,
            deliveryMode: mod.deliveryMode,
          };
        }) ?? [],
      periodicityOptions:
        periodicities?.map((p) => ({
          value: p,
          label: p,
        })) ?? [],
    };
  }, [activities, campuses, modules, responsibles, modalities, periodicities]);

  const defaultValues: ScheduleSectionForm = useMemo(() => {
    if (!schedule) {
      return {};
    }

    return {
      activity: getSelectedOption(
        schedule.activity.value,
        options.activityOptions,
      ),
      modality: getSelectedOption(
        schedule.modality?.value,
        options.modalityOptions,
      ),
      campus: getSelectedOption(schedule.campus?.value, options.campusOptions),
      classroom: schedule.classroom ?? undefined,
      day: getSelectedOption(schedule.day.value, daysOptions),
      startModule: getSelectedOption(
        schedule.startModule.value,
        options.moduleStartOptions,
      ),
      endModule: getSelectedOption(
        schedule.endModule?.value,
        options.moduleEndOptions,
      ),
      startDate: schedule.startDate
        ? dayjs(schedule.startDate).toDate()
        : undefined,
      endDate: schedule.endDate ? dayjs(schedule.endDate).toDate() : undefined,
      responsible: getSelectedOption(
        schedule.responsible?.account?.id,
        options.responsibleOptions,
      ),
      lessonsPeriodicity: getSelectedOption(
        schedule.lessonsPeriodicity?.value,
        options.periodicityOptions,
      ),
    };
  }, [options, daysOptions, schedule]);

  return (
    <ModalMaintainerSchedule
      defaultValues={defaultValues}
      sectionId={sectionId}
      sectionScheduleId={schedule?.id}
      closeModal={() => closeModal()}
      onSave={onSave}
      scheduleIndex={scheduleIndex}
      period={period}
      options={options}
    />
  );
};

export default ModalMaintainerSchedulePre;
